'use client';

/**
 * Stage whitelist picker, grouped by pipeline.
 *
 * Operator selects which stage ids the agent is allowed to move
 * opportunities INTO (`allowed_stage_codes`). Pipeline disambiguation
 * is handled at tool-schema level — the LLM always receives both
 * `stage` and `pipeline` as required parameters, so there is no
 * separate pipeline whitelist in this component.
 *
 * Stale stage ids render as grey chips with a one-click X.
 */

import { useMemo, useState } from 'react';
import { Check, X, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CrmPipeline, CrmTerminology } from '@/lib/crm/types';
import { useTranslations } from "next-intl"



async function copyText(text: string, label: string, tUi: ReturnType<typeof useTranslations<"AgentTools.AgentsSharedCrmStageMultiselect">>) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(tUi("copiedValue0", { value0: String(label) }));
  } catch {
    toast.error(tUi("copyFailed"));
  }
}

interface CrmStageMultiselectProps {
  pipelines: CrmPipeline[];
  /** Currently-selected stage ids (including stale ones). */
  stageValue: string[];
  /** Stage ids saved but no longer in the live pipelines. */
  staleStageCodes?: string[];
  onStageChange: (next: string[]) => void;
  /** Provider-specific terminology for pipeline/stage labels. */
  terminology: CrmTerminology;
  disabled?: boolean;
}

export function CrmStageMultiselect({
  pipelines,
  stageValue,
  staleStageCodes = [],
  onStageChange,
  terminology: t,
  disabled = false,
}: CrmStageMultiselectProps) {

  const tUi = useTranslations("AgentTools.AgentsSharedCrmStageMultiselect")

  const selectedStages = useMemo(() => new Set(stageValue), [stageValue]);
  const staleStageSet = useMemo(() => new Set(staleStageCodes), [staleStageCodes]);
  // Visible stale chips reflect the CURRENT draft, not the immutable
  // refresh-schema prop — so clicking X removes the chip immediately
  // instead of waiting for the next refresh.
  const visibleStaleStageCodes = useMemo(
    () => staleStageCodes.filter((c) => selectedStages.has(c)),
    [staleStageCodes, selectedStages]
  );

  // Accordion: only one pipeline expanded at a time, all collapsed by default.
  const [expandedPipelineId, setExpandedPipelineId] = useState<string | null>(null);

  const selectedStageCount = stageValue.filter((c) => !staleStageSet.has(c)).length;
  const allLiveStageCodes = useMemo(
    () => [
      ...new Set(
        pipelines.flatMap((pipeline) => pipeline.stages.map((stage) => stage.id))
      ),
    ],
    [pipelines]
  );
  const allLiveStagesSelected =
    allLiveStageCodes.length > 0
    && allLiveStageCodes.every((code) => selectedStages.has(code));

  function toggleStage(stageId: string) {
    if (disabled) return;
    if (selectedStages.has(stageId)) {
      onStageChange(stageValue.filter((c) => c !== stageId));
    } else {
      onStageChange([...stageValue, stageId]);
    }
  }

  function removeStaleStage(code: string) {
    if (disabled) return;
    onStageChange(stageValue.filter((c) => c !== code));
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">
          <span><span key={tUi("allowedTarget")}>{tUi("allowedTarget")}</span></span> {t.stagePlural.toLowerCase()}
        </div>
        <div className="text-xs text-muted-foreground tabular-nums">
          {selectedStageCount} <span><span key={tUi("selected")}>{tUi("selected")}</span></span>
        </div>
      </div>

      {pipelines.length === 0 ? (
        <div className="rounded-md border px-3 py-6 text-center text-sm text-muted-foreground">
          <span><span key={tUi("no")}>{tUi("no")}</span></span> {t.pipelinePlural.toLowerCase()} <span><span key={tUi("available")}>{tUi("available")}</span></span>
        </div>
      ) : (
        <div className="max-h-72 overflow-y-auto rounded-md border">
          <ul className="divide-y">
            {pipelines.map((pipeline) => {
              const isExpanded = expandedPipelineId === pipeline.id;
              const selectedInPipeline = pipeline.stages.filter(
                (s) => selectedStages.has(s.id)
              ).length;
              const pipelineCopyPayload =
                pipeline.stages.length > 0
                  ? `${pipeline.name}: ${pipeline.stages.map((s) => s.name).join(', ')}`
                  : pipeline.name;
              return (
                <li key={pipeline.id}>
                  <div className="flex items-center gap-1 bg-muted/30 pr-1">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedPipelineId(isExpanded ? null : pipeline.id)
                      }
                      aria-expanded={isExpanded}
                      aria-label={tUi("value0Value1Value2", { value0: String(pipeline.name), value1: String(pipeline.stages.length), value2: String(t.stagePlural.toLowerCase()) })}
                      className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      <div className="flex-1 truncate text-sm font-medium">
                        {pipeline.name}
                      </div>
                      {selectedInPipeline > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {selectedInPipeline}
                        </Badge>
                      )}
                      {pipeline.isDefault && (
                        <Badge variant="outline" className="text-xs">
                          <span key={tUi("default")}>{tUi("default")}</span></Badge>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => void copyText(pipelineCopyPayload, pipeline.name, tUi)}
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label={tUi("copyValue0", { value0: String(pipeline.name) })}
                      title={
                        pipeline.stages.length > 0
                          ? tUi("copyValue0NameValue1", { value0: String(t.pipeline.toLowerCase()), value1: String(t.stagePlural.toLowerCase()) })
                          : tUi("copyValue0Name", { value0: String(t.pipeline.toLowerCase()) })
                      }
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {isExpanded && (
                    <ul>
                      {pipeline.stages.map((stage) => {
                        const isSelected = selectedStages.has(stage.id);
                        return (
                          <li key={stage.id}>
                            <div className="flex items-center gap-1 pr-1 transition-colors hover:bg-muted/50">
                              <button
                                type="button"
                                onClick={() => toggleStage(stage.id)}
                                disabled={disabled}
                                className={cn(
                                  'flex min-w-0 flex-1 items-center gap-3 py-2 pl-10 pr-3 text-left text-sm',
                                  disabled && 'cursor-not-allowed opacity-50'
                                )}
                              >
                                <div
                                  className={cn(
                                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                                    isSelected
                                      ? 'border-primary bg-primary text-primary-foreground'
                                      : 'border-input'
                                  )}
                                >
                                  {isSelected && <Check className="h-3 w-3" />}
                                </div>
                                <div className="flex-1 truncate">{stage.name}</div>
                              </button>
                              <button
                                type="button"
                                onClick={() => void copyText(stage.name, stage.name, tUi)}
                                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                aria-label={tUi("copyValue0", { value0: String(stage.name) })}
                                title={tUi("copyValue0Name", { value0: String(t.stage.toLowerCase()) })}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {visibleStaleStageCodes.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">
            <span><span key={tUi("noLongerIn")}>{tUi("noLongerIn")}</span></span> {t.pipelinePlural.toLowerCase()}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {visibleStaleStageCodes.map((code) => (
              <Badge
                key={code}
                variant="outline"
                className="gap-1.5 border-dashed text-muted-foreground line-through"
              >
                {code}
                <button
                  type="button"
                  onClick={() => removeStaleStage(code)}
                  disabled={disabled}
                  aria-label={tUi("removeStageValue0", { value0: String(code) })}
                  className="ml-1 rounded-sm opacity-70 hover:opacity-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {!disabled && allLiveStageCodes.length > 0 && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          disabled={allLiveStagesSelected}
          onClick={() => {
            // Preserve stale ids so the operator can still remove them explicitly.
            onStageChange([...new Set([...stageValue, ...allLiveStageCodes])]);
          }}
        >
          <span key={tUi("selectAll")}>{tUi("selectAll")}</span></Button>
      )}
    </div>
  );
}
